// src/utils/secureMemoryUtils.js

/**
 * Utilities for secure memory handling of sensitive data like private keys and seed phrases
 * Prevents long-term storage in memory and mitigates memory inspection risks
 */

// Cache to store temporary access tokens that expire
const secureCache = new Map();

// Track memory cleanup timers
const cleanupTimers = new Map();

/**
 * Create a secure temporary reference to sensitive data
 * @param {string} sensitiveData - The sensitive data to temporarily store
 * @param {number} expirationMs - How long to keep in memory (default: 60 seconds)
 * @returns {string} A temporary token that can be used to access the data
 */
export const createSecureReference = (sensitiveData, expirationMs = 60000) => {
  if (!sensitiveData) return null;
  
  try {
    // Generate a secure random token as the reference
    const tokenBytes = new Uint8Array(16);
    window.crypto.getRandomValues(tokenBytes);
    const token = Array.from(tokenBytes)
      .map(b => b.toString(16).padStart(2, "0"))
      .join("");
    
    // Store the sensitive data with the token
    secureCache.set(token, sensitiveData);
    
    // Set up automatic cleanup
    const timerId = setTimeout(() => {
      secureClearData(token);
    }, expirationMs);
    
    cleanupTimers.set(token, timerId);
    
    return token;
  } catch (error) {
    console.error("Error creating secure reference:", error);
    return null;
  }
};

/**
 * Get sensitive data using a secure reference token
 * @param {string} token - The reference token
 * @param {boolean} keepAlive - Whether to keep the reference alive (default: false)
 * @param {number} expirationMs - New expiration time if keeping alive
 * @returns {string|null} The sensitive data or null if expired/invalid
 */
export const getSecureData = (token, keepAlive = false, expirationMs = 60000) => {
  if (!token || !secureCache.has(token)) return null;
  
  // Get the sensitive data
  const sensitiveData = secureCache.get(token);
  
  if (!keepAlive) {
    // Immediately clear after access unless keepAlive is true
    secureClearData(token);
  } else {
    // Reset the timer if keeping alive
    if (cleanupTimers.has(token)) {
      clearTimeout(cleanupTimers.get(token));
    }
    
    const timerId = setTimeout(() => {
      secureClearData(token);
    }, expirationMs);
    
    cleanupTimers.set(token, timerId);
  }
  
  return sensitiveData;
};

/**
 * Securely clear sensitive data from memory
 * Uses memory overwriting to minimize data residue
 * @param {string} token - The reference token to clear
 */
export const secureClearData = (token) => {
  if (!token || !secureCache.has(token)) return;
  
  try {
    // Get the data to overwrite it
    let sensitiveData = secureCache.get(token);
    
    // Overwrite the data in memory with random values
    // This helps minimize data residue in memory
    if (typeof sensitiveData === 'string' && sensitiveData.length > 0) {
      const length = sensitiveData.length;
      const randomChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()";
      
      // Overwrite the value multiple times with different random values
      for (let i = 0; i < 3; i++) {
        let randomString = "";
        for (let j = 0; j < length; j++) {
          randomString += randomChars.charAt(Math.floor(Math.random() * randomChars.length));
        }
        sensitiveData = randomString;
      }
      
      // Final zero-fill
      sensitiveData = "0".repeat(length);
    }
    
    // Clear the timeout if it exists
    if (cleanupTimers.has(token)) {
      clearTimeout(cleanupTimers.get(token));
      cleanupTimers.delete(token);
    }
    
    // Delete from cache
    secureCache.delete(token);
  } catch (error) {
    console.error("Error clearing secure data:", error);
  }
};

/**
 * Securely clear all sensitive data from memory
 */
export const clearAllSecureData = () => {
  try {
    // Get all tokens
    const tokens = Array.from(secureCache.keys());
    
    // Clear each token
    tokens.forEach(token => {
      secureClearData(token);
    });
  } catch (error) {
    console.error("Error clearing all secure data:", error);
  }
};

/**
 * Create a function to safely access sensitive data for a single operation
 * @param {function} operation - Function that receives the sensitive data
 * @param {string} token - Token for the sensitive data
 * @returns {function} - A wrapped function that securely handles the data
 */
export const createSecureOperation = (operation, token) => {
  return () => {
    const data = getSecureData(token);
    if (data) {
      try {
        return operation(data);
      } catch (error) {
        console.error("Error in secure operation:", error);
        throw error;
      }
    } else {
      throw new Error("Sensitive data is no longer available");
    }
  };
};

// Add event listeners to clear sensitive data when tab/window becomes inactive
if (typeof window !== 'undefined') {
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      clearAllSecureData();
    }
  });
  
  window.addEventListener('pagehide', clearAllSecureData);
  window.addEventListener('beforeunload', clearAllSecureData);
}

/**
 * Creates a self-destructing callback for displaying sensitive information
 * @param {string} sensitiveData - The sensitive data
 * @param {number} timeoutMs - How long to allow access (default: 60 seconds)
 * @returns {Object} - Object with token and access function
 */
export const createTemporaryAccess = (sensitiveData, timeoutMs = 60000) => {
  const token = createSecureReference(sensitiveData, timeoutMs);
  
  const access = () => {
    return getSecureData(token, true, timeoutMs);
  };
  
  const destroy = () => {
    secureClearData(token);
  };
  
  return { access, destroy };
};

/**
 * Mask a sensitive string for display (e.g., "word1 word2 word3" -> "w••• w••• w•••")
 * @param {string} text - Text to mask
 * @returns {string} - Masked text
 */
export const maskSensitiveText = (text) => {
  if (!text) return "";
  
  // For seed phrases (space-separated words)
  if (text.includes(" ")) {
    return text
      .split(" ")
      .map(word => word.charAt(0) + "•".repeat(word.length > 1 ? word.length - 1 : 0))
      .join(" ");
  }
  
  // For private keys or other long strings
  if (text.length > 12) {
    return text.substring(0, 6) + "•".repeat(text.length - 12) + text.substring(text.length - 6);
  }
  
  // For short strings
  return text.charAt(0) + "•".repeat(text.length - 2) + text.charAt(text.length - 1);
};